import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AlertTriangle,
  FilePenLine,
  Info,
  Pencil,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";

import { ReactComponent as LoadingIcon } from "@/assets/svg/loading-spinner.svg";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import {
  SnipAnchorRect,
  SnipResultPopover,
} from "@/components/FilePreview/SnipResultPopover";
import { useAppForm } from "@/components/Form";
import { MapToReportBuilderDialog } from "@/components/MapToReportBuilderDialog";
import { Transcriber, TranscriberRef } from "@/components/Transcriber";
import { isRecordingSessionLive } from "@/components/Transcriber/active-session";
import {
  TextInsertionOptions,
  useTextareaTranscriptionHandler,
} from "@/components/Transcriber/hooks";
import {
  applyPartialReplacement,
  isPartialRangeIntact,
} from "@/components/Transcriber/partials";
import type { LiveTranscriptTurn } from "@/components/Transcriber/segments";
import { ScrollArea } from "@/components/ui/scroll-area";
import { isAssemblyAIScribe } from "@/constants/live-transcription";
import {
  useQueryTranscript,
  useRetryTranscription,
} from "@/hooks/mutations/use-transcription";
import { useCaseByCaseNumber } from "@/hooks/queries/use-cases";
import { useTranscription } from "@/pages/ReportStudio/ReportBuilder/store/useTranscription";
import { setMessage } from "@/redux/notification";
import {
  resumeTranscriptionSummaryTask,
  startTranscriptionSummaryTask,
} from "@/redux/transcription-summary";
import {
  claimRecording,
  discardRecording,
  prepareRecording,
  releaseRecording,
} from "@/services/recordingOwnership";
import { type RootState, store } from "@/store";

import {
  // AssessmentColumnNameEnum,
  AssessmentResultStatusEnum,
  BodySaveAssessmentApiV1AssessmentPost,
  BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch,
} from "@api/generated/types.gen";

import { AssemblyAIRecoveryPrompt } from "@components/RecordingDock/AssemblyAIRecoveryPrompt";
import { RecordingRecoveryPrompt } from "@components/RecordingDock/RecordingRecoveryPrompt";
import { useRecordingDock } from "@components/RecordingDock/store/useRecordingDock";
import { Button } from "@components/ui/button";
import { Card } from "@components/ui/card";
import { Checkbox } from "@components/ui/checkbox";
import { Input } from "@components/ui/input";
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/ui/tooltip";

import {
  useCreateAssessment,
  useUpdateAssessment,
  useUpdateAssessmentField,
} from "@hooks/mutations/use-assessments";
import { useAssessmentByCaseNumber } from "@hooks/queries/use-assessments";

import { showError, showInfo } from "@utils/notification";
import { cn } from "@utils/ui";
import { matchSnippetToBlocks } from "@utils/wordOverlapMatch";

import { FETCHING_MESSAGE, NO_CONTENT_MESSAGE } from "../constants/messages";
import {
  ASSESSMENT_SECTIONS,
  ASSESSMENT_STATUS,
  RESETTABLE_ASSESSMENT_FIELDS,
} from "../constants/sections";
import type { RecoveredSession } from "../hooks/assemblyAIRecovery";
import { useAutoExport } from "../hooks/useAutoExport";
import { NOOP_CALL_AUDIO_SHARE } from "../hooks/useCallAudioShare";
// import { AcceptButton } from "./AcceptButton";
import { RecordingSession } from "../hooks/useRecordingSession";
import {
  AudioTranscriptFormData,
  createAudioTranscriptSchema,
} from "../schema/audio-transcript";
import { matchSnippetToSegments } from "../utils/matchSnippetToSegments";
// import { ExportAssessmentConfirmation } from "./ExportAssessmentConfirmation";
import { speakerTypeFor } from "../utils/speakerType";
import { buildTranscriptResetPayload } from "../utils/transcript";
import { LiveTranscriptionSegment } from "./LiveTranscriptionFeed";
import { RecordingCompleteBanner } from "./RecordingCompleteBanner";
import { RecordingNotSentBanner } from "./RecordingNotSentBanner";
import { ScribeSession, formatDuration } from "./ScribeSession";
import { TranscribeAndUploadButtons } from "./TranscribeAndUploadButtons";
import { TranscriptEntryData, TranscriptEntryList } from "./TranscriptEntry";
import { TranscriptionFailedBanner } from "./TranscriptionFailedBanner";
import { toLiveFeedSegments } from "./live-feed-segments";

/**
 * Type for segmented transcript entry from the API
 * Matches the backend SegmentedTranscriptEntrySchema
 */
type SegmentedTranscriptEntry = {
  speaker: string;
  timestamp: [number, number]; // [start, end] in seconds
  timestamp_hhmmss: [string, string]; // Formatted timestamps
  text: string;
};

/**
 * Shape of a single segment inside a `query-transcript` search hit.
 * The backend's TranscriptQueryResponseSchema.result is an untyped Cosmos
 * payload ({ [key: string]: unknown }), so this describes only the fields
 * handleSearch actually reads off each segment.
 */
type TranscriptQuerySegment = {
  timestamp_display?: string;
  timestamp?: (string | number)[];
  speaker?: string;
  text?: string;
};

type TranscriptQueryResultItem = {
  segments?: TranscriptQuerySegment[];
};

// SCRIBE-07 back-link: splits a flat section's content into sentence-ish
// blocks with their starting character offset, so a fuzzy content-word match
// (matchSnippetToBlocks) can be turned back into a scroll position within
// the original string.
const splitIntoBlocksWithOffsets = (
  text: string
): { text: string; start: number }[] => {
  const blocks: { text: string; start: number }[] = [];
  const regex = /[^.!?\n]+[.!?\n]*/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(text)) !== null) {
    if (match[0].trim()) {
      blocks.push({ text: match[0], start: match.index });
    }
  }
  return blocks;
};

// SCRIBE-07 back-link: whether a fuzzy word-overlap match's block indices
// form one contiguous run (e.g. [2,3,4]) rather than disconnected hits
// scattered across the content (e.g. [1,7,12]). matchSnippetToBlocks/
// matchSnippetToSegments return every block clearing the overlap bar, which
// is exactly right for "this snippet spans several adjacent blocks" but is
// also what generic/repeated phrasing scattered across unrelated blocks
// produces — a real passage of any length is contiguous; scattered hits
// aren't, and shouldn't be highlighted with equal confidence.
const isContiguousRun = (indices: number[]): boolean => {
  if (indices.length === 0) return false;
  const sorted = [...indices].sort((a, b) => a - b);
  return sorted[sorted.length - 1] - sorted[0] === sorted.length - 1;
};

// Turns a backend stage key (e.g. "role_resolution_s") into a display label
// ("Role resolution"). Generic on purpose — new stages the backend adds
// don't need a matching frontend change to show up in the breakdown.
const formatTimingStageLabel = (key: string): string => {
  const withoutSuffix = key.endsWith("_s") ? key.slice(0, -2) : key;
  const words = withoutSuffix.split("_").join(" ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/* ─── Plain text content (non-transcript sections + plain mode) ─── */
const PlainTranscriptContent = ({
  isFetching,
  sectionContent,
  isAssessmentNotesSection,
  isHighlighted,
}: {
  isFetching: boolean;
  sectionContent: string;
  isAssessmentNotesSection: boolean;
  /** SCRIBE-07 back-link: briefly flashed when a mapped note's text was
   * relocated to this section — see the relocate effect in AudioTranscript. */
  isHighlighted?: boolean;
}) => {
  const isEmpty = !sectionContent || sectionContent === "null";
  return (
    <p
      className={cn(
        "whitespace-pre-wrap text-xs leading-[18px] transition-colors",
        isEmpty && "italic text-foreground-muted",
        isHighlighted && "rounded bg-primary/15 ring-1 ring-primary/30"
      )}
    >
      {isFetching
        ? FETCHING_MESSAGE
        : isEmpty
          ? isAssessmentNotesSection
            ? "You haven't added any notes yet."
            : NO_CONTENT_MESSAGE
          : sectionContent}
    </p>
  );
};

/* ─── Detailed transcript view with search results ─── */
const DetailedTranscriptContent = ({
  isSearching,
  searchError,
  searchResults,
  detailedEntries,
  detailedViewFilter,
  highlightedEntryId,
  onSearchResultClick,
  isLive = false,
  isTranscribing = false,
  isFailed = false,
}: {
  isSearching: boolean;
  searchError: string | null;
  searchResults: {
    answer?: string;
    searchResults?: TranscriptEntryData[];
  } | null;
  detailedEntries: TranscriptEntryData[];
  detailedViewFilter: string;
  highlightedEntryId: string | null;
  onSearchResultClick: (entry: TranscriptEntryData) => void;
  /** A live recording session is in progress (drives the empty state). */
  isLive?: boolean;
  /** Batch transcription is still running (drives the empty state). */
  isTranscribing?: boolean;
  /** Transcription failed (drives the empty state). */
  isFailed?: boolean;
}) => {
  if (isSearching) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="flex flex-col items-center gap-3">
          <LoadingIcon className="size-8 animate-spin-ccw" />
          <span className="text-sm text-foreground-muted">
            Searching transcript...
          </span>
        </div>
      </div>
    );
  }

  if (searchError) {
    return (
      <Card className="p-4">
        <div className="flex flex-col items-center gap-2 text-center">
          <p className="text-sm font-semibold text-destructive">Error</p>
          <p className="text-sm text-foreground-muted">{searchError}</p>
        </div>
      </Card>
    );
  }

  const searchResultEntries = searchResults?.searchResults ?? [];
  const hasSearchResults = searchResultEntries.length > 0;

  return (
    <div className="space-y-6 text-sm">
      {/* Answer */}
      {searchResults?.answer && (
        <Card className="gap-1 space-y-2 p-4">
          <h3 className="text-sm font-semibold text-primary">Answer</h3>
          <div className="border-b border-border" />
          <p
            className="text-sm leading-relaxed text-foreground-muted"
            dangerouslySetInnerHTML={{
              __html: searchResults.answer.replace(
                /\*\*(.*?)\*\*/g,
                "<strong>$1</strong>"
              ),
            }}
          />
        </Card>
      )}

      {/* No answer */}
      {searchResults && !searchResults.answer && (
        <Card className="p-4">
          <p className="text-center text-sm text-foreground-muted">
            No answer found for this query.
          </p>
        </Card>
      )}

      {/* Search results list */}
      {hasSearchResults && (
        <div className="space-y-2 py-2">
          <h3 className="pb-4 text-sm font-semibold text-foreground">
            Search Results ({searchResultEntries.length} found)
          </h3>
          <TranscriptEntryList
            entries={searchResultEntries}
            filter={detailedViewFilter}
            isSearchResults={true}
            onEntryClick={onSearchResultClick}
          />
        </div>
      )}

      {/* Search returned answer but no entries */}
      {searchResults?.answer && !hasSearchResults && (
        <div className="space-y-2 py-2">
          <h3 className="pb-4 text-sm font-semibold text-foreground">
            Search Results (0 found)
          </h3>
          <Card className="p-4">
            <p className="text-center text-sm text-foreground-muted">
              No matching transcript entries found.
            </p>
          </Card>
        </div>
      )}

      {/* Regular entries — no active search */}
      {!searchResults?.searchResults && (
        <>
          {detailedEntries.length > 0 ? (
            <TranscriptEntryList
              entries={detailedEntries}
              filter={detailedViewFilter}
              highlightedEntryId={highlightedEntryId}
            />
          ) : isLive ? (
            <Card className="flex h-full items-center justify-center p-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <div className="rounded-full bg-primary-50 p-3">
                  <div className="size-3 animate-pulse rounded-full bg-destructive" />
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-foreground">
                    Listening for speech…
                  </p>
                  <p className="max-w-md text-sm text-foreground-muted">
                    Speaker-labelled turns will appear here as the assessment is
                    recorded.
                  </p>
                </div>
              </div>
            </Card>
          ) : (
            /* Not live and nothing to show. Which of the three blurbs applies
               is driven by the assessment/field status — these used to be a
               standalone <AlertBanner> below the panel, which sat far from the
               empty card it was explaining. */
            <Card className="flex h-full items-center justify-center p-6">
              <div className="flex flex-col items-center gap-3 text-center">
                <div
                  className={cn(
                    "rounded-full p-3",
                    isFailed ? "bg-destructive-50" : "bg-muted"
                  )}
                >
                  {isTranscribing ? (
                    <LoadingIcon className="size-5 animate-spin-ccw" />
                  ) : isFailed ? (
                    <AlertTriangle className="size-5 text-destructive" />
                  ) : (
                    <Info className="size-5 text-foreground-muted" />
                  )}
                </div>
                <div className="space-y-1">
                  <p className="font-medium text-foreground">
                    {isTranscribing
                      ? "Finalising transcript"
                      : isFailed
                        ? "Transcription failed"
                        : "No transcript yet"}
                  </p>
                  <p className="max-w-md text-sm text-foreground-muted">
                    {isTranscribing
                      ? "We're finalising the transcript for this recording. Speaker-labelled turns will appear here shortly."
                      : isFailed
                        ? "We couldn't transcribe this recording. Try uploading the audio again, or record a new session."
                        : "Record or upload audio for this assessment and the speaker-labelled transcript will appear here."}
                  </p>
                </div>
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

type AudioTranscriptProps = {
  caseNumber?: string;
  sectionId?: string;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  onDirtyChange?: (isDirty: boolean) => void;
  /**
   * The live recording session, read from the store — owned by the
   * root RecordingHost. When active, the content panel shows the
   * in-panel ScribeSession instead of the section editor; when stopped, a
   * completion banner sits above the full-transcript viewer. The diarized
   * summarize on stop is dispatched by the host, not here.
   */
  session?: RecordingSession;
  /**
   * The extract text of a mapped Report Builder note, arriving via the
   * SCRIBE-07 back-link's in-memory handoff (pendingHighlightHandoff.ts) —
   * deliberately NOT a URL param, since this is clinical/PHI content. When
   * set, once this section's content has loaded, we try to relocate +
   * highlight the original text against the CURRENT content (no position is
   * persisted — see AudioTranscriber/utils/matchSnippetToSegments.ts).
   */
  initialHighlightText?: string;
};

/** Raw speaker id (persisted, batch-diarized) -> display name. */
type SpeakerRoleMap = Record<string, string>;

/**
 * Pure derivation for committing a speaker rename (see the "View By" row's
 * commitSpeakerRename): trims the input, treats empty/unchanged as a no-op,
 * and returns the updated map plus enough context (prevDisplay/trimmed) for
 * the caller to follow an active filter and persist the change. Returns null
 * for anything that isn't a real rename, so the caller can bail in one check.
 */
export const renameSpeaker = (
  speakerRoleMap: SpeakerRoleMap,
  raw: string,
  value: string
): { nextRoleMap: SpeakerRoleMap; prevDisplay: string; trimmed: string } | null => {
  const trimmed = value.trim();
  if (!trimmed) return null;

  const prevDisplay = speakerRoleMap[raw] ?? raw;
  if (prevDisplay === trimmed) return null;

  return {
    nextRoleMap: { ...speakerRoleMap, [raw]: trimmed },
    prevDisplay,
    trimmed,
  };
};

export const AudioTranscript = ({
  caseNumber,
  sectionId,
  onDirtyChange,
  session,
  initialHighlightText,
}: AudioTranscriptProps) => {
  const dispatch = useDispatch<typeof store.dispatch>();

  // When the AudioRecorder modal owns the session, suppress live writes
  // into this section. The streaming partials still accumulate inside
  // the Transcriber (getFullTranscript) and the Done flow fires
  // /transcription/summarize — that's the only path that should populate
  // the field during a modal-driven recording.
  const isModalSession = useTranscription((s) => s.isModalSession);

  const { data: currentAssessment, isFetching } = useAssessmentByCaseNumber({
    caseNumber,
  });

  const { data: caseData } = useCaseByCaseNumber({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  // The recording-session Transcriber ref comes from the root RecordingHost via
  // the store — used to read the accumulated transcript for the
  // form mirror on session end. Section dictation uses its own local ref.
  const recordingTranscriberRef = useRecordingDock((s) => s.transcriberRef);

  // SCRIBE-05: suppress the audio-recovery prompt while a live session is
  // already recording/paused for THIS case — an orphan from an earlier
  // crash must never be confused with (or surfaced alongside) a session
  // that's currently in progress.
  const isRecordingLiveForThisCase = useRecordingDock(
    (s) => s.ownsCase(caseNumber) && s.isActive()
  );
  // A remount can race the store sync (MDRS-233) — the `session` prop itself
  // already reflects non-idle before the store catches up. Checked
  // separately from the store-based selector above so that race window is
  // still covered.
  const isSessionPropInFlight = !!session && session.status !== "idle";
  // Bumped to look again for a recording to recover (MDRS-551): remounting the
  // prompts re-runs their lookups, e.g. after one was closed with Esc.
  const recoveryCheckRequestedAt = useRecordingDock(
    (s) => s.recoveryCheckRequestedAt
  );
  const recoveryCheckKey = `${caseNumber}#${recoveryCheckRequestedAt}`;
  // Azure recovery prompt waits for this so the two prompts never open stacked.
  // Holds the check it settled for, so a re-check waits for the new settle.
  const [assemblyAIRecoverySettledFor, setAssemblyAIRecoverySettledFor] = useState<
    string | null
  >(null);

  // Ref for the per-section dictation Transcriber. Separate from the
  // recording-session ref so section dictation and the persistent recording
  // never share one Transcriber instance.
  const localTranscriberRef = useRef<TranscriberRef>(null);

  // Current assessment now already includes field-specific statuses
  const assessmentWithFieldStatuses = currentAssessment;

  const updateAssessmentField = useUpdateAssessmentField();
  const updateAssessment = useUpdateAssessment();
  const createAssessment = useCreateAssessment();

  // Auto-export functionality for assessment fields
  const { triggerAutoExport } = useAutoExport({
    assessmentData: currentAssessment,
    assessmentId: currentAssessment?.id,
    caseNumber: caseNumber || "",
    enabled: !!currentAssessment && !!caseNumber,
  });

  const [isEditing, setIsEditing] = useState(false);
  const [detailedViewFilter, setDetailedViewFilter] = useState<string>("all");
  // Plain, timestamped turns built live while the session is recording.
  // UI-ONLY: a real-time viewing aid shown while the session is active — not
  // diarized, and NOT persisted. The backend's batch diarization path is the
  // single writer of segmented_full_transcript, which becomes the source of
  // (speaker-labelled) truth once the session ends and the batch result loads.
  const [liveTurns, setLiveTurns] = useState<LiveTranscriptTurn[] | null>(null);
  // Raw speaker id (from the persisted, batch-diarized transcript) -> display
  // name. Seeded from the persisted mapping (MDRS-154, see the effect below)
  // and updated by manual renames (see the "View By" row), which work
  // uniformly against whatever raw speaker string is present. See
  // speaker_role_mapping.md.
  const [speakerRoleMap, setSpeakerRoleMap] = useState<SpeakerRoleMap>({});
  // Which raw speaker label is currently being renamed via the inline
  // edit control, and its in-progress input value. Null when not editing.
  const [editingSpeaker, setEditingSpeaker] = useState<{
    raw: string;
    value: string;
  } | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState<{
    answer?: string;
    searchResults?: Array<{
      timestamp: string;
      speaker: string;
      speakerType: "doctor" | "patient";
      content: string;
      pauseDuration?: number;
    }>;
  } | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  // Bumped on every search/clear so a stale response can't repopulate results.
  const searchRequestIdRef = useRef(0);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [highlightedEntryId, setHighlightedEntryId] = useState<string | null>(null);
  // SCRIBE-07 back-link: flashes the whole plain-content `<p>` (Assessment
  // Notes / AI-summary / clinical sections) when a mapped note's text is
  // relocated there. Coarser than the segment-level Transcript highlight
  // above (v1 scope — see matchSnippetToSegments.ts's doc comment) but a
  // real jump, not a no-op.
  const [isPlainContentHighlighted, setIsPlainContentHighlighted] = useState(false);
  const scrollAreaViewportRef = useRef<HTMLDivElement>(null);
  const [selectedText, setSelectedText] = useState("");
  // Viewport rect of the highlighted transcript text, so the "Map to Report"
  // popover anchors beside the selection instead of the panel corner.
  const [selectionRect, setSelectionRect] = useState<SnipAnchorRect | null>(null);
  const [mapDialogText, setMapDialogText] = useState("");
  const [isMapDialogOpen, setIsMapDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

  // ── In-panel Scribe session UI state (MDRS-116) ──
  // Assessment Notes are shared with the floating dock via the store and
  // autosaved by the root RecordingHost. The scribe Notes field just
  // reads/writes the same value + reflects the store's save state.
  const sessionNotes = useRecordingDock((s) => s.notes);
  const setSessionNotes = useRecordingDock((s) => s.setNotes);
  const notesSaveState = useRecordingDock((s) => s.notesSaveState);
  const notesLastSavedAt = useRecordingDock((s) => s.notesLastSavedAt);
  const priorFeedRows = useRecordingDock((s) => s.priorRows);
  const resumeDockSession = useRecordingDock((s) => s.resumeSession);
  // MDRS-551: the stopped session's upload, and Retry / Download / Discard for it.
  const summarize = useRecordingDock((s) => s.summarize);
  const dockControls = useRecordingDock((s) => s.controls);
  // The caret toggles the waveform (supersedes MDRS-85's collapse-to-pill).
  const [waveformCollapsed, setWaveformCollapsed] = useState(false);
  // Latest interim (in-progress) hypothesis for the live feed's greyed line.
  const [livePartial, setLivePartial] = useState("");
  // Stop / Cancel confirmations for the live session controls.
  const [isStopSessionOpen, setIsStopSessionOpen] = useState(false);
  const [isCancelSessionOpen, setIsCancelSessionOpen] = useState(false);
  // Duration snapshot shown on the completion banner (state D).
  const [completedDuration, setCompletedDuration] = useState("00:00:00");

  const isSessionLive = !!session?.isActive;
  const isSessionStopped = !!session?.isStopped;
  const isSessionConnecting = !!session?.isConnecting;

  // When returning to the scribe tab mid-session, this component
  // remounts with empty `liveTurns` — the feed would flash blank until the
  // next turn lands. Seed it once from the always-mounted host Transcriber's
  // accumulated turns and in-progress partial. `isRestoringFeed` covers the
  // case where the host has neither yet.
  const [isRestoringFeed, setIsRestoringFeed] = useState(false);
  const feedSeededRef = useRef(false);
  // Started while this page was mounted (fresh or resumed): nothing to restore.
  const sawSessionConnectingRef = useRef(false);
  useEffect(() => {
    if (isSessionConnecting) sawSessionConnectingRef.current = true;
  }, [isSessionConnecting]);
  const getDockElapsedMs = useRecordingDock((s) => s.elapsedMs);
  useEffect(() => {
    if (feedSeededRef.current) return;
    if (!isSessionLive) return;
    feedSeededRef.current = true;
    const seeded = recordingTranscriberRef?.current?.getTurns() ?? [];
    const seededPartial = recordingTranscriberRef?.current?.getPartial?.() ?? "";
    if (seeded.length > 0) setLiveTurns(seeded);
    if (seededPartial.trim()) setLivePartial(seededPartial);
    if (
      seeded.length === 0 &&
      !seededPartial.trim() &&
      !sawSessionConnectingRef.current &&
      getDockElapsedMs() > 3000
    ) {
      // No segments yet, but the persistent host session has clearly been
      // running for a while — this mount is a return to an in-progress
      // session (tab switch back), not the start of a new one, so there's
      // genuinely something to wait on restoring. A brand-new session (this
      // component mounting at ~0 elapsed) has nothing to "restore" — that
      // case falls through to the normal "Listening for speech…" empty
      // state instead of the misleading restoring copy.
      setIsRestoringFeed(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSessionLive]);

  // Notify parent when edit mode changes
  useEffect(() => {
    onDirtyChange?.(isEditing);
  }, [isEditing, onDirtyChange]);

  // Reset all UI state when navigating to a different section. Only the
  // per-section dictation Transcriber is stopped here — the
  // persistent recording session (root RecordingHost) must keep running as the
  // doctor moves between sections.
  useEffect(() => {
    if (localTranscriberRef.current?.isTranscribing) {
      localTranscriberRef.current.stopTranscription();
    }
    setIsEditing(false);
    setDetailedViewFilter("all");
    setSearchQuery("");
    setSearchResults(null);
    setSearchError(null);
    searchRequestIdRef.current += 1;
    setIsSearching(false);
    setHighlightedEntryId(null);
    setSelectedText("");
    setMapDialogText("");
    setIsMapDialogOpen(false);
    // Re-read live turns from the host (this also runs on mount, after the seed).
    // speakerRoleMap is deliberately NOT reset here — it's assessment-level
    // (persisted, see MDRS-154), not section-level, so switching tabs must
    // not lose a manual rename.
    const hostTurns = isSessionLive
      ? (recordingTranscriberRef?.current?.getTurns() ?? [])
      : [];
    setLiveTurns(hostTurns.length > 0 ? hostTurns : null);
    setEditingSpeaker(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset on section change only
  }, [sectionId]);

  // Not remounted across a case switch, so drop the previous case's search, filter and highlight.
  useEffect(() => {
    setDetailedViewFilter("all");
    setSearchQuery("");
    setSearchResults(null);
    setSearchError(null);
    searchRequestIdRef.current += 1;
    setIsSearching(false);
    setHighlightedEntryId(null);
  }, [caseNumber]);

  // Seed speakerRoleMap from the persisted mapping (MDRS-154) whenever the
  // assessment loads or is refetched. This component isn't remounted across
  // a case switch (no `key` on <AudioTranscript> in AudioTranscriber/index.tsx),
  // so `currentAssessment.id` changing is the only signal that we've moved to
  // a different case's assessment — `prev` at that point belongs to the OLD
  // case and must be replaced, not merged, or a stale rename (e.g. raw label
  // "A") would leak into (and get persisted onto) an unrelated case. Only
  // when the id is unchanged (a refetch of the SAME assessment, e.g. the one
  // our own save in commitSpeakerRename triggers) do local edits win.
  const seededAssessmentIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!currentAssessment?.id) return;
    const persisted = currentAssessment.speaker_role_mapping;
    const isSameAssessment = seededAssessmentIdRef.current === currentAssessment.id;
    seededAssessmentIdRef.current = currentAssessment.id;

    if (isSameAssessment) {
      if (persisted && Object.keys(persisted).length > 0) {
        setSpeakerRoleMap((prev) => ({ ...persisted, ...prev }));
      }
    } else {
      setSpeakerRoleMap(persisted ?? {});
    }
  }, [currentAssessment?.id, currentAssessment?.speaker_role_mapping]);

  // Resume batch-summary polling on load when an assessment is still
  // TRANSCRIBING. The VM result is persisted ONLY inside the status-poll loop,
  // so if the doctor closed the tab / refreshed while it was processing, nothing
  // polls the status endpoint and the summary + diarized transcript are lost
  // (the row stays stuck at TRANSCRIBING). Re-attaching a poller here recovers it.
  // Guards: only if the task isn't already being polled (activeTasks — e.g. the
  // tab that started it), and once per task id (ref) so a poll-driven activeTasks
  // update can't re-dispatch.
  const activeTranscriptionTasks = useSelector(
    (state: RootState) => state.transcription?.activeTasks || {}
  );
  const resumedTaskIdRef = useRef<string | null>(null);
  useEffect(() => {
    const taskId = currentAssessment?.task_id;
    if (
      currentAssessment?.status !== "TRANSCRIBING" ||
      !taskId ||
      activeTranscriptionTasks[taskId] ||
      resumedTaskIdRef.current === taskId
    ) {
      return;
    }
    resumedTaskIdRef.current = taskId;
    dispatch(
      resumeTranscriptionSummaryTask({
        taskId,
        caseNumber: caseData?.case_number ?? caseNumber ?? null,
        examineeName: caseData?.examinee_name ?? "",
      })
    );
  }, [
    currentAssessment?.status,
    currentAssessment?.task_id,
    activeTranscriptionTasks,
    caseData?.case_number,
    caseData?.examinee_name,
    caseNumber,
    dispatch,
  ]);

  const queryTranscript = useQueryTranscript();

  const sectionTitle = useMemo(() => {
    const selectedFeature = ASSESSMENT_SECTIONS.find(
      (item) => item.id === sectionId
    );
    return selectedFeature ? selectedFeature.title : "";
  }, [sectionId]);

  const sectionContent = useMemo(() => {
    const selectedFeature = ASSESSMENT_SECTIONS.find(
      (item) => item.id === sectionId
    );

    if (!selectedFeature || !selectedFeature.key || !currentAssessment) return "";

    // Get the field from assessment
    const field = currentAssessment[selectedFeature.key];

    // Handle the new field structure (which is an object with content and status)
    let content = "";
    if (field) {
      if (typeof field === "object" && "content" in field) {
        // New structure - field is an AssessmentFieldStatusSchema object
        content = field.content;
      } else if (typeof field === "string") {
        // Old structure for backward compatibility
        content = field;
      }
    }

    if (content === "null") content = "";
    return content;
  }, [currentAssessment, sectionId]);

  const sectionKey = useMemo(() => {
    const selectedFeature = ASSESSMENT_SECTIONS.find(
      (item) => item.id === sectionId
    );
    return selectedFeature?.key;
  }, [sectionId]);

  // MDRS-575: the server keeps a copy of the recording only until it's
  // transcribed, so a stored copy means "not transcribed yet" — still true after
  // the doctor edits notes following a failure (status EDITED). Re-run it
  // without the doctor uploading again.
  const retryTranscription = useRetryTranscription();
  const canRetryTranscription =
    !!caseNumber &&
    !!currentAssessment?.has_stored_audio &&
    currentAssessment?.status !== ASSESSMENT_STATUS.TRANSCRIBING &&
    !isSessionLive &&
    !isSessionConnecting &&
    !isSessionStopped;
  const handleRetryTranscription = useCallback(async () => {
    if (!caseNumber) return;
    try {
      await retryTranscription.mutateAsync(caseNumber);
    } catch (err) {
      showError(
        err instanceof Error
          ? err.message
          : "Couldn't retry the transcription. Try again.",
        dispatch
      );
    }
  }, [caseNumber, retryTranscription, dispatch]);
  // A retry resets every transcript-derived section (notes are kept), so ask
  // first if the doctor has typed into any since the failure. The confirmation
  // holds the case it was opened for: this page stays mounted across case
  // switches, and one left open while the doctor goes back in history must not
  // reset the case they land on.
  const [retryConfirmCase, setRetryConfirmCase] = useState<string | null>(null);
  if (retryConfirmCase !== null && retryConfirmCase !== caseNumber) {
    setRetryConfirmCase(null);
  }
  const retryWouldClearSections = useMemo(
    () =>
      RESETTABLE_ASSESSMENT_FIELDS.some(
        (field) => field !== "notes" && !!currentAssessment?.[field]?.content?.trim()
      ),
    [currentAssessment]
  );
  // An open editor ignores refetches (see the form-reset effect below), so its
  // draft would sit over the regenerated section and a later Save would write
  // it back. Notes survive a retry, so their editor doesn't block it.
  const isEditingResettableSection =
    isEditing &&
    sectionKey !== "notes" &&
    RESETTABLE_ASSESSMENT_FIELDS.some((field) => field === sectionKey);
  const requestRetryTranscription = useCallback(() => {
    if (isEditingResettableSection) {
      showInfo(
        "Save or cancel your changes to this section, then retry the transcription.",
        dispatch
      );
      return;
    }
    if (retryWouldClearSections) {
      setRetryConfirmCase(caseNumber ?? null);
      return;
    }
    void handleRetryTranscription();
  }, [
    isEditingResettableSection,
    retryWouldClearSections,
    caseNumber,
    handleRetryTranscription,
    dispatch,
  ]);
  const handleRetryConfirm = useCallback(() => {
    setRetryConfirmCase(null);
    if (retryConfirmCase !== caseNumber) return;
    void handleRetryTranscription();
  }, [retryConfirmCase, caseNumber, handleRetryTranscription]);

  // Get field-specific status if available
  const fieldStatus = useMemo(() => {
    if (!sectionKey || !assessmentWithFieldStatuses) return undefined;

    // Find the field in the assessment with field statuses
    const field =
      assessmentWithFieldStatuses[
        sectionKey as keyof typeof assessmentWithFieldStatuses
      ];

    // If it's an object with a status property, return that status.
    // Cast needed: speaker_role_mapping's index-signature type structurally
    // satisfies `"status" in field` too, so TS can't narrow `field` down to
    // just AssessmentFieldStatusSchema here — sectionKey is always a real
    // report-section key (see ASSESSMENT_SECTIONS), never "speaker_role_mapping".
    if (field && typeof field === "object" && "status" in field) {
      return field.status as AssessmentResultStatusEnum;
    }

    // Otherwise fall back to the overall assessment status
    return currentAssessment?.status;
  }, [assessmentWithFieldStatuses, sectionKey, currentAssessment?.status]);

  const isAssessmentNotes = sectionKey === "notes";

  const detailedEntries = useMemo((): TranscriptEntryData[] => {
    // Always the persisted, batch-diarized transcript — the only source of
    // speaker-labelled text now that live transcription isn't diarized.
    const rawSegments = currentAssessment?.segmented_full_transcript;
    if (!rawSegments || !Array.isArray(rawSegments) || rawSegments.length === 0) {
      return [];
    }

    const segments = rawSegments as SegmentedTranscriptEntry[];

    return segments.map((segment, index, arr) => {
      const rawSpeaker = segment.speaker || "Unknown";
      const speaker = speakerRoleMap[rawSpeaker] ?? rawSpeaker;
      const speakerType = speakerTypeFor(speaker);

      let pauseDuration: number | undefined;
      if (index < arr.length - 1) {
        const currentEnd = segment.timestamp?.[1] || 0;
        const nextStart = arr[index + 1].timestamp?.[0] || 0;
        const gap = nextStart - currentEnd;
        // Only show pause if it's significant (> 0.5 seconds)
        if (gap > 0.5) {
          pauseDuration = gap;
        }
      }

      // Format timestamp - use the start time from timestamp_hhmmss or format from seconds
      // TODO: ensure format in the future, ask backend.
      let timestamp = "0:00";
      if (segment.timestamp_hhmmss?.[0]) {
        // Remove leading hour zero for cleaner display (e.g., "00:05:30" -> "5:30") but keep MM:SS zeros
        timestamp = segment.timestamp_hhmmss[0].replace(/^00:/, "") || "0:00";
      } else if (segment.timestamp?.[0] !== undefined) {
        // Fallback: format seconds to MM:SS
        const totalSeconds = Math.floor(segment.timestamp[0]);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = totalSeconds % 60;
        timestamp = `${minutes}:${seconds.toString().padStart(2, "0")}`;
      }

      return {
        timestamp,
        speaker,
        speakerType,
        content: segment.text || "",
        pauseDuration,
      };
    });
  }, [currentAssessment?.segmented_full_transcript, speakerRoleMap]);

  // Unique speakers for the "View By" filter row, as { raw, display } pairs —
  // `raw` is the underlying segment.speaker value (needed to key a rename
  // into speakerRoleMap), `display` is what's shown/filtered on (role- or
  // user-renamed, or the raw persisted name otherwise). Order preserved as
  // first-seen.
  const uniqueSpeakers = useMemo((): { raw: string; display: string }[] => {
    const rawSegments = currentAssessment?.segmented_full_transcript;
    if (!rawSegments || !Array.isArray(rawSegments) || rawSegments.length === 0) {
      return [];
    }

    const segments = rawSegments as SegmentedTranscriptEntry[];
    const seen = new Set<string>();
    const result: { raw: string; display: string }[] = [];

    segments.forEach((segment) => {
      const raw = segment.speaker;
      if (raw && !seen.has(raw)) {
        seen.add(raw);
        result.push({ raw, display: speakerRoleMap[raw] ?? raw });
      }
    });

    return result;
  }, [currentAssessment?.segmented_full_transcript, speakerRoleMap]);

  // Commit an in-progress speaker rename (see the "View By" row) into
  // speakerRoleMap. Empty input is treated as a cancel, not a rename to
  // "" — the speaker keeps whatever name it had before editing started.
  const commitSpeakerRename = useCallback(() => {
    if (!editingSpeaker) return;
    const { raw, value } = editingSpeaker;
    setEditingSpeaker(null);

    const renamed = renameSpeaker(speakerRoleMap, raw, value);
    if (!renamed) return;

    setSpeakerRoleMap(renamed.nextRoleMap);
    // If the active filter pointed at this speaker's old display name,
    // follow the rename so the filter doesn't silently start matching
    // nothing.
    if (detailedViewFilter === renamed.prevDisplay) {
      setDetailedViewFilter(renamed.trimmed);
    }

    // Persist the rename (MDRS-154) so it survives a section switch or a
    // page navigation, not just this render. Fire-and-forget: renames are
    // already gated on blur/Enter (not per-keystroke), so no debounce is
    // needed here.
    if (currentAssessment?.id && currentAssessment?.case_id) {
      updateAssessmentField.mutate({
        assessmentId: currentAssessment.id,
        caseId: currentAssessment.case_id,
        caseNumber,
        field: "speaker_role_mapping",
        value: JSON.stringify(renamed.nextRoleMap),
      });
    }
  }, [
    editingSpeaker,
    speakerRoleMap,
    detailedViewFilter,
    currentAssessment,
    caseNumber,
    updateAssessmentField,
  ]);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // STP-88: streaming-dictation partials are written directly into the
  // textarea and replaced in place as AssemblyAI refines each turn — so
  // the doctor sees words appear as they speak instead of waiting for
  // end_of_turn. These refs track where the active partial lives.
  // partialStartOffsetRef:  start offset of the in-progress partial in
  //                         the textarea, or null when no turn is open.
  // partialLengthRef:       length of the last partial we wrote, so the
  //                         next partial knows what to replace.
  // lastPartialTextRef:     the exact text we wrote, used to detect
  //                         user-edits inside the range and abandon it
  //                         gracefully if so.
  const partialStartOffsetRef = useRef<number | null>(null);
  const partialLengthRef = useRef<number>(0);
  const lastPartialTextRef = useRef<string>("");

  const resetPartialRange = useCallback(() => {
    partialStartOffsetRef.current = null;
    partialLengthRef.current = 0;
    lastPartialTextRef.current = "";
  }, []);

  const form = useAppForm({
    defaultValues: {
      content: sectionContent || "",
    } as AudioTranscriptFormData,
    validators: {
      onBlur: createAudioTranscriptSchema(isAssessmentNotes),
    },
    onSubmit: async ({ value }) => {
      // Stop the section dictation if it's active — never the persistent
      // recording session.
      if (localTranscriberRef.current?.isTranscribing) {
        localTranscriberRef.current.stopTranscription();
      }

      if (!sectionKey || !caseNumber) {
        return;
      }

      try {
        // Ensure we're not sending 'null' as a string - use empty string instead
        const contentValue =
          value.content === null || value.content === undefined ? "" : value.content;

        if (currentAssessment?.id && currentAssessment?.case_id) {
          await updateAssessmentField.mutateAsync({
            assessmentId: currentAssessment.id,
            caseId: currentAssessment.case_id,
            caseNumber: caseNumber,
            field: sectionKey as keyof Omit<
              BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch,
              "case_id"
            >,
            value: contentValue,
            status: "EDITED",
          });
        } else {
          // No assessment row exists yet for this case (user is editing
          // a section before any recording/upload has happened). Create
          // one with this field populated so the save isn't lost.
          await createAssessment.mutateAsync({
            case_number: caseNumber,
            [sectionKey]: contentValue,
          } as BodySaveAssessmentApiV1AssessmentPost);
        }

        setIsEditing(false);

        if (sectionKey !== "full_transcript") {
          // Trigger auto-export for fields that are not yet exported
          triggerAutoExport();
        } else {
          if (caseData) {
            const formData = new FormData();
            formData.append("case_number", caseData?.case_number || "");
            formData.append("examinee_name", caseData?.examinee_name || "");
            formData.append("full_transcript", contentValue || "");

            if (!contentValue?.trim()) return;
            await claimRecording();
            try {
              const submissionId = await prepareRecording(
                caseNumber!,
                currentAssessment?.submission_id ?? null,
                true
              );
              formData.append("submission_id", submissionId);
              try {
                await dispatch(startTranscriptionSummaryTask(formData));
              } catch (error) {
                // Free the server reservation too, or other tabs see this case busy until it expires.
                await discardRecording().catch(() => {});
                throw error;
              }
            } finally {
              releaseRecording();
            }
          }
        }
      } catch (error) {
        console.error("Failed to update assessment field:", error);
      }
    },
  });

  // Update form values when section content changes — but NOT while the user
  // has unsaved edits in the form (`isEditing`). Otherwise a background refetch
  // that changes `sectionContent` (e.g. a resume-poll completion invalidating
  // the assessment query) silently clobbers in-progress edits — including a
  // just-restored crash-recovery snapshot, re-losing the exact text MDRS-217
  // sets out to protect. Switching sections resets `isEditing` first (the
  // section-nav effect above), so section changes still repopulate; `isEditing`
  // is in the deps so the reset runs the moment editing ends (save/cancel).
  useEffect(() => {
    if (isEditing) return;
    form.reset({
      content: sectionContent || "",
    });
  }, [sectionContent, isEditing, form]);

  // Base handler for real-time transcription
  const baseTranscriptionHandler = useTextareaTranscriptionHandler(
    textareaRef,
    (val) => {
      form.setFieldValue("content", val as string | undefined);
    },
    (val) => {
      form.setFieldValue("content", val as string | undefined);
    }
  );

  // STP-88: write each partial directly into the textarea and replace
  // it in place as AssemblyAI refines its hypothesis. Finalise on
  // end_of_turn (which arrives via onTextInsert with `final + " "`).
  //
  // The fallback path — when the textarea isn't mounted, or the user has
  // edited inside our active partial range — defers to the base handler
  // so we don't clobber their typing.
  const transcriptionHandler = {
    ...baseTranscriptionHandler,
    // Plain, timestamped turns from the live session. Independent of the
    // textarea/modal-session plumbing — we always surface them so the live
    // feed updates as the assessment is recorded.
    onTurnsUpdate: (turns: LiveTranscriptTurn[]) => {
      setLiveTurns(turns);
      setIsRestoringFeed(false);
      // A finalised turn just landed — clear the interim line so it isn't
      // duplicated beneath the committed turns (MDRS-116).
      setLivePartial("");
    },
    onPartialUpdate: (partial: string) => {
      // Modal-driven sessions don't write through to the section — the
      // Transcriber still accumulates internally for the Done flow. Capture
      // the interim hypothesis, though, so the in-panel live feed can show the
      // greyed "in-progress" line with a cursor (MDRS-116).
      if (isModalSession) {
        setLivePartial(partial);
        if (partial) setIsRestoringFeed(false);
        return;
      }

      const textarea = textareaRef.current;
      if (!textarea) return;

      if (!isEditing) {
        setIsEditing(true);
      }

      // Empty partial is an "end of partial" hint from the Transcriber.
      // The onTextInsert path will already have committed the final text
      // (or the silence-modal path will have stopped the session), so
      // just close the range.
      if (partial === "") {
        resetPartialRange();
        return;
      }

      const current = textarea.value;

      // First partial of a fresh turn: anchor at the current cursor.
      if (partialStartOffsetRef.current === null) {
        // Bug fix: when the silence-detection modal opens and then
        // closes, Radix restores focus asynchronously. If the first
        // partial after silence arrives before focus has fully
        // returned to the textarea, an unfocused textarea reports
        // `selectionStart === 0` in some browsers — anchoring the
        // next partial at the very start of the text instead of
        // where the doctor was actually dictating. Re-focus first so
        // `selectionStart` reads the real cursor position.
        if (document.activeElement !== textarea) {
          textarea.focus();
        }
        partialStartOffsetRef.current = textarea.selectionStart;
        partialLengthRef.current = 0;
        lastPartialTextRef.current = "";
      }

      const start = partialStartOffsetRef.current;
      const oldLength = partialLengthRef.current;

      // If the user has typed into our partial range, surrender it — the
      // next partial will start a fresh range at the new cursor.
      if (
        !isPartialRangeIntact({
          current,
          start,
          oldLength,
          lastPartial: lastPartialTextRef.current,
        })
      ) {
        resetPartialRange();
        return;
      }

      const { text, newLength } = applyPartialReplacement({
        current,
        start,
        oldLength,
        partial,
      });

      form.setFieldValue("content", text);
      partialLengthRef.current = newLength;
      lastPartialTextRef.current = partial;

      // Restore cursor at end of partial after the form re-renders.
      setTimeout(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        const cursor = start + newLength;
        ta.setSelectionRange(cursor, cursor);
      }, 0);
    },
    onTextInsert: (text: string, eventData: TextInsertionOptions) => {
      // Modal-driven sessions don't write through to the section.
      if (isModalSession) return;

      if (!isEditing) {
        setIsEditing(true);
      }

      const textarea = textareaRef.current;
      // No active partial range OR no textarea — defer to the base
      // handler, which performs the usual cursor-insert.
      if (textarea == null || partialStartOffsetRef.current === null) {
        baseTranscriptionHandler.onTextInsert(text, eventData);
        return;
      }

      const current = textarea.value;
      const start = partialStartOffsetRef.current;
      const oldLength = partialLengthRef.current;

      // User typed into the range while a partial was in flight — defer
      // to the base handler so we don't overwrite their edits.
      if (
        !isPartialRangeIntact({
          current,
          start,
          oldLength,
          lastPartial: lastPartialTextRef.current,
        })
      ) {
        resetPartialRange();
        baseTranscriptionHandler.onTextInsert(text, eventData);
        return;
      }

      // Replace the partial range with the finalised text and close the
      // range. The next partial will anchor at the new cursor position.
      const { text: nextValue } = applyPartialReplacement({
        current,
        start,
        oldLength,
        partial: text,
      });
      form.setFieldValue("content", nextValue);
      resetPartialRange();

      setTimeout(() => {
        const ta = textareaRef.current;
        if (!ta) return;
        const cursor = start + text.length;
        ta.setSelectionRange(cursor, cursor);
        ta.focus();
      }, 0);
    },
    onFocus: () => {
      // Modal-driven sessions shouldn't focus the section textarea or
      // flip it into edit mode — the user is looking at the dialog.
      if (isModalSession) return;

      // Auto-enter edit mode when focusing for transcription
      if (!isEditing) {
        setIsEditing(true);
      }

      // Call the original handler if it exists
      if (baseTranscriptionHandler.onFocus) {
        baseTranscriptionHandler.onFocus();
      }
    },
    onEnd: () => {
      resetPartialRange();
      // If the session ended without committing any text — typically a
      // failed start — undo the optimistic setIsEditing(true) from
      // onFocus. Otherwise the user is left with an editable textarea
      // pre-loaded with the existing transcript, and the next dictation
      // inserts at the cursor instead of replacing the field as the
      // overwrite confirmation implied.
      // Dictation-completion check reads the per-section dictation Transcriber
      //; the recording session's transcript is read separately on
      // stop via the shared store ref.
      const accumulated = localTranscriberRef.current?.getFullTranscript() ?? "";
      if (!accumulated.trim()) {
        setIsEditing(false);
      }
      if (baseTranscriptionHandler.onEnd) {
        baseTranscriptionHandler.onEnd();
      }
    },
  };

  // Feed the host's always-mounted Transcriber this section's live handler
  // so it drives the on-tab live view. `transcriptionHandler` is a
  // fresh object each render, so register a STABLE ref-backed wrapper once and
  // keep the ref current — otherwise the store would churn every render/tick.
  const transcriptionHandlerRef = useRef(transcriptionHandler);
  transcriptionHandlerRef.current = transcriptionHandler;
  const registerLiveCallback = useRecordingDock((s) => s.registerLiveCallback);
  useEffect(() => {
    registerLiveCallback({
      onFocus: () => transcriptionHandlerRef.current.onFocus?.(),
      onEnd: () => transcriptionHandlerRef.current.onEnd?.(),
      onTextInsert: (text, opts) =>
        transcriptionHandlerRef.current.onTextInsert(text, opts),
      onPartialUpdate: (partial) =>
        transcriptionHandlerRef.current.onPartialUpdate?.(partial),
      onTurnsUpdate: (turns) =>
        transcriptionHandlerRef.current.onTurnsUpdate?.(turns),
    });
    return () => registerLiveCallback(null);
  }, [registerLiveCallback]);

  const blurb = useMemo(() => {
    switch (sectionKey) {
      case "full_transcript":
        return (
          <p className="max-w-4xl text-xs text-foreground-muted">
            This is an auto-generated transcript from the assessment recording and
            may contain errors. Summaries are generated based on the content of this
            transcript.
          </p>
        );
      case "notes":
        return (
          <div className="max-w-4xl text-xs text-foreground-muted">
            You can transcribe any additional notes or comments regarding the
            assessment here. 
          </div>
        );
      default:
        return (
          <p className="max-w-4xl text-xs text-foreground-muted">
            Always check the summary for accuracy. You can edit the summary. Click
            Save after editing to send the updated summary to Compose.
          </p>
        );
    }
  }, [sectionKey]);

  const tooltipContent = useMemo(() => {
    switch (sectionKey) {
      case "full_transcript":
        return (
          <section className="space-y-2 text-xs">
            <h1 className="text-sm font-semibold">Full Transcript</h1>
            <p className="text-foreground-light-muted">
              This is the transcript generated from the assessment recording.
              Summaries are generated based on the content of this transcript.
            </p>
            <p className="font-medium">
              Important: the transcript is auto-generated and may contain errors.
              Details may be misheard, misspelled, omitted, or hallucinated.
            </p>
            <p className="text-foreground-light-muted">
              Always review and critically assess the outputs.
            </p>
          </section>
        );
      case "notes":
        return (
          <section className="text-xs">
            <h1 className="text-sm font-semibold">Assessment Notes</h1>
            <p className="text-foreground-light-muted">
              This section is designed to provide you with a place to record
              additional notes, thoughts and comments. Notes added here are not
              automatically exported to Compose or included in your final report,
              unless you manually add them.
            </p>
          </section>
        );
    }
  }, [sectionKey]);

  const handleCancel = () => {
    // Stop the section dictation if it's active — never the persistent
    // recording session.
    if (localTranscriberRef.current?.isTranscribing) {
      localTranscriberRef.current.stopTranscription();
    }

    form.reset({
      content: sectionContent || "",
    });
    setIsEditing(false);
  };

  const toggleEditing = () => {
    if (isEditing) {
      handleCancel();
    } else {
      setIsEditing(true);
    }
  };

  const isAssessmentNotesSection = sectionKey === "notes";
  const isFullTranscript = sectionKey === "full_transcript";

  // The in-panel live recording view (ScribeSession) belongs to the Transcript
  // section only. On any other section the recording keeps running —
  // surfaced by the floating dock — while that section shows its own content.
  // AssemblyAI: also while connecting, so start-up doesn't flash the placeholder card.
  const showLiveView =
    (isSessionLive || (isAssemblyAIScribe() && isSessionConnecting)) &&
    isFullTranscript;

  // Publish whether the live Transcript view is on screen so the floating dock
  // hides while it's the surface and shows on other sections.
  const setTranscriptViewActive = useRecordingDock((s) => s.setTranscriptViewActive);
  useEffect(() => {
    setTranscriptViewActive(showLiveView);
    return () => setTranscriptViewActive(false);
  }, [showLiveView, setTranscriptViewActive]);

  // Assessment Notes autosave now lives in the root RecordingHost
  // so it fires from both the scribe Notes field and the floating
  // dock, on or off the scribe tab. This component just reads/writes the shared
  // store value + reflects its save state.

  // Recording session ended → UI reconciliation on the scribe tab.
  // The diarized summarize task is dispatched by the root RecordingHost (so it
  // fires even when finishing from the dock off-tab); here we only reconcile
  // the visible section view when the page IS mounted:
  //   1) exit edit mode so Cancel/Save + the nav warning aren't stuck on,
  //   2) mirror the accumulated transcript into the section field so the
  //      doctor sees it and can edit/save it.
  // (Notes autosave + its flush-on-stop live in the RecordingHost now.)
  const sawStoppedRef = useRef(false);
  useEffect(() => {
    const stopped = session?.status === "stopped";
    if (!stopped) {
      sawStoppedRef.current = false;
      return;
    }
    if (sawStoppedRef.current) return;
    sawStoppedRef.current = true;

    setIsEditing(false);

    const fromTranscriber =
      recordingTranscriberRef?.current?.getFullTranscript() ?? "";
    const fromForm = (form.getFieldValue("content") || "").trim();
    const transcriptContent = fromTranscriber || fromForm;

    if (transcriptContent && transcriptContent !== fromForm) {
      form.setFieldValue("content", transcriptContent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.status]);

  // Scroll to highlighted entry after search results are cleared and regular list re-renders
  useEffect(() => {
    if (!highlightedEntryId) return;
    const el = document.getElementById(highlightedEntryId);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "center" });
    }
    const timer = setTimeout(() => setHighlightedEntryId(null), 3000);
    return () => clearTimeout(timer);
  }, [highlightedEntryId]);

  // SCRIBE-07 back-link: relocate a mapped Report Builder note's text
  // against THIS section's current content and highlight it. No position is
  // persisted anywhere for a mapped extract — only its text — so this is a
  // best-effort relocate at click time (exact match first, then fuzzy
  // content-word overlap), not a stored offset that could go stale the
  // moment the source is edited. Runs once per navigation (guarded by the
  // ref), and only once this section's content has actually loaded.
  const initialHighlightAttemptedRef = useRef(false);
  useEffect(() => {
    if (!initialHighlightText) return;
    if (initialHighlightAttemptedRef.current) return;
    if (isFetching) return;
    initialHighlightAttemptedRef.current = true;

    let matched = false;

    if (isFullTranscript) {
      const contents = detailedEntries.map((e) => e.content);
      let matchIndex = contents.findIndex(
        (c) => c.includes(initialHighlightText) || initialHighlightText.includes(c)
      );
      if (matchIndex === -1) {
        const fuzzyMatches = matchSnippetToSegments(contents, initialHighlightText);
        // A contiguous run of segments is a confident multi-segment passage
        // (the same shape a manual selection spanning segments would
        // produce, of any length); matches scattered across DISCONNECTED
        // segments mean the overlap is too generic to trust — land on the
        // tab without guessing which one is right.
        if (fuzzyMatches && isContiguousRun(fuzzyMatches)) {
          matchIndex = fuzzyMatches[0];
        }
      }
      if (matchIndex !== -1) {
        matched = true;
        setHighlightedEntryId(`transcript-entry-${matchIndex}`);
      }
    } else if (sectionContent) {
      let matchStart = sectionContent.indexOf(initialHighlightText);
      if (matchStart === -1) {
        const blocks = splitIntoBlocksWithOffsets(sectionContent);
        const fuzzyMatches = matchSnippetToBlocks(
          blocks.map((b) => b.text),
          initialHighlightText
        );
        if (fuzzyMatches && isContiguousRun(fuzzyMatches)) {
          matchStart = blocks[fuzzyMatches[0]].start;
        }
      }
      if (matchStart !== -1) {
        matched = true;
        const viewport = scrollAreaViewportRef.current;
        if (viewport) {
          const ratio = matchStart / sectionContent.length;
          viewport.scrollTop = Math.max(
            0,
            ratio * (viewport.scrollHeight - viewport.clientHeight)
          );
        }
        setIsPlainContentHighlighted(true);
        setTimeout(() => setIsPlainContentHighlighted(false), 3000);
      }
    }

    if (!matched) {
      showInfo(
        "Couldn't find the exact original text — it may have been edited since it was mapped.",
        dispatch
      );
    }
    // Only re-run if the text/section identity actually changes; intentionally
    // excludes dispatch (stable enough) from re-triggering.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    initialHighlightText,
    isFetching,
    isFullTranscript,
    detailedEntries,
    sectionContent,
  ]);

  const handleSearchResultClick = (entry: TranscriptEntryData) => {
    const matchIndex = detailedEntries.findIndex(
      (e) => e.content === entry.content && e.speaker === entry.speaker
    );
    resetSearchResults();
    setSearchQuery("");
    setDetailedViewFilter("all");
    if (matchIndex !== -1) {
      setHighlightedEntryId(`transcript-entry-${matchIndex}`);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() || !caseNumber) return;

    const requestId = ++searchRequestIdRef.current;
    setSearchError(null);
    setSearchResults(null);
    setHighlightedEntryId(null);
    setIsSearching(true);

    try {
      const response = await queryTranscript.mutateAsync({
        case_number: caseNumber,
        query: searchQuery.trim(),
      });
      if (requestId !== searchRequestIdRef.current) return;
      // Handle the response structure - result can be a dict with answer and results
      const result = response.result || {};

      // The API returns "results" (not "search_results" or "searchResults")
      const resultsArray: TranscriptQueryResultItem[] = Array.isArray(result.results)
        ? (result.results as TranscriptQueryResultItem[])
        : [];

      // Transform results to extract segments[0] and map to TranscriptEntryData format
      const transformedResults = resultsArray
        .map((resultItem): TranscriptEntryData | null => {
          // Get the first segment from each result
          const segment = resultItem.segments?.[0];
          if (!segment) return null;

          // MDRS-538: hits can carry the original label; show the renamed one.
          const speaker = segment.speaker
            ? (speakerRoleMap[segment.speaker] ?? segment.speaker)
            : "Unknown";

          // Map segment data to TranscriptEntryData format
          return {
            timestamp:
              segment.timestamp_display ||
              (Array.isArray(segment.timestamp)
                ? segment.timestamp.join(":")
                : "") ||
              "",
            speaker,
            speakerType: speakerTypeFor(speaker),
            content: segment.text || "",
            pauseDuration: undefined, // Add if available in segment data
          };
        })
        .filter((item): item is TranscriptEntryData => item !== null); // Remove null entries

      setSearchResults({
        answer: typeof result.answer === "string" ? result.answer : undefined,
        searchResults: transformedResults,
      });
    } catch (error) {
      if (requestId !== searchRequestIdRef.current) return;
      console.error("Failed to search transcript:", error);
      setSearchResults(null);
      setSearchError("Something went wrong, please try again later.");
    } finally {
      if (requestId === searchRequestIdRef.current) setIsSearching(false);
    }
  };

  const resetSearchResults = () => {
    searchRequestIdRef.current += 1;
    setIsSearching(false);
    setSearchResults(null);
    setSearchError(null);
  };

  const handleClearSearch = () => {
    setSearchQuery("");
    resetSearchResults();
    // The X button unmounts once the query is empty, so keep focus in the search bar.
    searchInputRef.current?.focus();
  };

  // Any edit makes the current results stale; the full transcript shows until the next search.
  const handleSearchQueryChange = (value: string) => {
    setSearchQuery(value);
    resetSearchResults();
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !isSearching) {
      handleSearch();
    }
  };

  const templateId = caseData?.template?.id;

  const handleMouseUp = useCallback(() => {
    if (isEditing) return;
    const selection = window.getSelection();
    const text = selection?.toString().trim();
    if (text) {
      setSelectedText(text);
      // Anchor the popover to the selection's bounding rect (viewport coords).
      const rect = selection?.rangeCount
        ? selection.getRangeAt(0).getBoundingClientRect()
        : null;
      setSelectionRect(
        rect && (rect.width || rect.height)
          ? {
              top: rect.top,
              bottom: rect.bottom,
              left: rect.left,
              right: rect.right,
            }
          : null
      );
    }
  }, [isEditing]);

  const handleSelectionClose = useCallback(() => {
    setSelectedText("");
    setSelectionRect(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  const handleMapToReportBuilder = useCallback(() => {
    if (!selectedText) return;
    setMapDialogText(selectedText);
    setSelectedText("");
    setSelectionRect(null);
    window.getSelection()?.removeAllRanges();
    setIsMapDialogOpen(true);
  }, [selectedText]);

  // MDRS-399 — "Delete transcript" blanks the transcript AND every
  // summary/report-section column derived from it (previously it only
  // cleared the transcript, MDRS-86, leaving stale summaries behind), in a
  // single bulk PATCH rather than hard-deleting the assessment row.
  const handleDeleteTranscript = useCallback(async () => {
    if (!currentAssessment?.id || !currentAssessment?.case_id) {
      setIsDeleteDialogOpen(false);
      return;
    }

    try {
      await updateAssessment.mutateAsync({
        assessmentId: currentAssessment.id,
        caseNumber,
        updateData: {
          case_id: currentAssessment.case_id,
          ...buildTranscriptResetPayload(),
        },
      });
      dispatch(setMessage({ content: "Transcript deleted", type: "success" }));
    } catch {
      // useUpdateAssessment logs the underlying error in its onError.
      dispatch(
        setMessage({ content: "Failed to delete transcript", type: "error" })
      );
    } finally {
      setIsDeleteDialogOpen(false);
    }
  }, [currentAssessment, caseNumber, updateAssessment, dispatch]);

  // ── In-panel Scribe session wiring (MDRS-116) ──

  // Elapsed session timer. Reads the SINGLE store clock — the same
  // drift-free, pause-aware clock the floating dock shows — so the in-panel and
  // dock timers never diverge. A 1s interval only forces a re-render; the value
  // is always computed from the store's clock.
  const dockElapsedMs = useRecordingDock((s) => s.elapsedMs);
  const [sessionElapsedMs, setSessionElapsedMs] = useState(() => dockElapsedMs());

  useEffect(() => {
    setSessionElapsedMs(dockElapsedMs());
    if (session?.status === "stopped") {
      setCompletedDuration(formatDuration(dockElapsedMs()));
    }
    if (session?.status !== "recording") return;
    const interval = setInterval(() => setSessionElapsedMs(dockElapsedMs()), 1000);
    return () => clearInterval(interval);
  }, [session?.status, dockElapsedMs]);

  // Reset the connecting-time waveform/partial UI (the clock itself is reset in
  // the store by beginSession).
  useEffect(() => {
    if (session?.status === "connecting") {
      setWaveformCollapsed(false);
      setLivePartial("");
    }
  }, [session?.status]);

  const liveFeedSegments = useMemo(
    (): LiveTranscriptionSegment[] => [
      ...priorFeedRows,
      ...toLiveFeedSegments(liveTurns),
    ],
    [priorFeedRows, liveTurns]
  );

  // Continues an interrupted AssemblyAI recording: same audio, a new transcript segment.
  // Uses the store session directly: the page's wrapped start() would re-claim via beginSession.
  // Keeps the recovery prompt mounted (spinner) while the resumed session connects.
  const [isResumingRecording, setIsResumingRecording] = useState(false);
  const handleResumeRecording = useCallback(
    async (recovered: RecoveredSession): Promise<boolean> => {
      const storeSession = useRecordingDock.getState().session;
      if (!caseNumber || !storeSession) return false;
      // The prompt's mount guard only covers this case; another case may own the dock.
      if (isRecordingSessionLive()) {
        showError(
          "Another assessment recording is in progress. Finish it before resuming this one.",
          dispatch
        );
        return false;
      }
      const priorRows: LiveTranscriptionSegment[] = (recovered.feed ?? []).map(
        (row, index) => ({ ...row, key: `prior:${index}` })
      );
      if (priorRows.length > 0) {
        priorRows.push({
          text: "",
          timestamp: "",
          key: "prior:resume",
          divider: true,
        });
      }
      resumeDockSession({
        caseNumber,
        examineeName: recovered.examineeName ?? caseData?.examinee_name ?? null,
        elapsedMs: recovered.resumeOffsetMs,
        notes: currentAssessment?.notes ?? "",
        priorRows,
      });
      const start = (callAudioTrack?: MediaStreamTrack) =>
        storeSession.start(recovered.speakerHints, {
          caseNumber,
          mode: callAudioTrack ? "telehealth" : "in_person",
          callAudioTrack,
          micDeviceId: recovered.micDeviceId,
          resumeFrom: recovered,
        });
      setIsResumingRecording(true);
      let resumed = false;
      try {
        // Called straight from the Resume click so the picker gets the user gesture.
        const share =
          recovered.mode === "telehealth"
            ? useRecordingDock.getState().callAudioShare
            : null;
        if (!share) {
          resumed = await start();
          return resumed;
        }
        resumed = await share.share().then(
          (track) => start(track),
          (err: unknown) => {
            const cancelled =
              err instanceof DOMException && err.name === "NotAllowedError";
            showInfo(
              cancelled
                ? "Call tab not shared — recording the microphone only."
                : err instanceof Error
                  ? `${err.message} Recording the microphone only.`
                  : "Couldn't share the call tab — recording the microphone only.",
              dispatch
            );
            return start();
          }
        );
        return resumed;
      } finally {
        setIsResumingRecording(false);
        // A failed resume must not leave the dock claimed with the old case's prior rows.
        const dock = useRecordingDock.getState();
        if (!resumed && dock.caseNumber === caseNumber && dock.status === "idle") {
          dock.endSession();
        }
      }
    },
    [
      caseNumber,
      caseData?.examinee_name,
      currentAssessment?.notes,
      resumeDockSession,
      dispatch,
    ]
  );

  // The interim (in-progress) line: the latest partial hypothesis.
  const interimSegment = useMemo((): LiveTranscriptionSegment | null => {
    if (!livePartial.trim()) return null;
    return {
      text: livePartial,
      timestamp: formatDuration(sessionElapsedMs),
    };
  }, [livePartial, sessionElapsedMs]);

  // Telehealth "Re-share call tab" (MDRS-268) — re-opens the share picker and
  // wires the fresh track straight into the live audio graph via the shared
  // recording-session Transcriber ref, without restarting the session.
  const callAudioShare =
    useRecordingDock((s) => s.callAudioShare) ?? NOOP_CALL_AUDIO_SHARE;

  // useRecordingSession only owns the MediaRecorder/transcriber graph — it
  // never touches callAudioShare (that hook is owned by the root
  // RecordingHost so it survives scribe-tab navigation, see its own doc
  // comment). Stopping/cancelling the session must release the shared tab
  // here too, or the browser's "sharing this tab" indicator (and the tab's
  // audio capture) is left running indefinitely after Finish Recording.
  const handleSessionStopConfirm = useCallback(() => {
    setIsStopSessionOpen(false);
    session?.stop();
    if (callAudioShare.status !== "idle") {
      callAudioShare.stop();
    }
  }, [session, callAudioShare]);

  const handleSessionCancelConfirm = useCallback(() => {
    setIsCancelSessionOpen(false);
    session?.cancel();
    if (callAudioShare.status !== "idle") {
      callAudioShare.stop();
    }
  }, [session, callAudioShare]);
  const handleReshareCallAudio = useCallback(async () => {
    try {
      await callAudioShare.share();
    } catch (err) {
      // The picker throws on user cancellation too — mirrors
      // AudioRecorder.tsx's handleToggleShare, which treats this the same
      // way: not a real error, leave the "Call audio stopped" banner as-is
      // rather than surfacing a toast for a deliberate dismissal.
      if (err instanceof DOMException && err.name === "NotAllowedError") return;
      console.error("[AudioTranscript] Failed to re-share call tab:", err);
      showError(
        err instanceof Error
          ? err.message
          : "Couldn't share the call tab. Try again.",
        dispatch
      );
      return;
    }
    const track = useRecordingDock.getState().callAudioShare?.track;
    if (track) {
      recordingTranscriberRef?.current?.replaceCallAudioTrack(track);
    }
  }, [callAudioShare, recordingTranscriberRef, dispatch]);

  // The isolated mic track for the "You" source card's level meter (MDRS-268).
  // getMicTrack() is imperative, not reactive, so it can't be read during
  // render (React ref rules) — sync it into state from an effect instead,
  // re-reading whenever mediaRecorder changes since both become available
  // together at session start (and both go null together on stop/cancel).
  const [micTrack, setMicTrack] = useState<MediaStreamTrack | null>(null);
  useEffect(() => {
    setMicTrack(
      session?.mediaRecorder
        ? (recordingTranscriberRef?.current?.getMicTrack() ?? null)
        : null
    );
  }, [session?.mediaRecorder, recordingTranscriberRef]);

  return (
    <>
      {showLiveView && (
        <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
          <Card
            as="section"
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-border bg-background-white p-0"
          >
            <ScribeSession
              isPaused={!!session?.isPaused}
              sessionMode={session?.sessionMode ?? "in_person"}
              callAudioLost={
                session?.sessionMode === "telehealth" &&
                callAudioShare.status === "lost"
              }
              onReshareCallAudio={handleReshareCallAudio}
              micTrack={micTrack}
              callAudioTrack={callAudioShare.track}
              callAudioSourceLabel={callAudioShare.sourceLabel}
              elapsedMs={sessionElapsedMs}
              mediaRecorder={session?.mediaRecorder ?? null}
              inputDeviceLabel={session?.inputDeviceLabel ?? "Default microphone"}
              segments={liveFeedSegments}
              interim={interimSegment}
              isRestoringFeed={isRestoringFeed}
              isConnecting={isSessionConnecting}
              // Hidden: AssemblyAI speaker labels reset per connection, so they mismatch after a reconnect.
              showLiveSpeakers={false}
              notes={sessionNotes}
              onNotesChange={setSessionNotes}
              notesSaveState={notesSaveState}
              notesLastSavedAt={notesLastSavedAt}
              waveformCollapsed={waveformCollapsed}
              onToggleWaveform={() => setWaveformCollapsed((prev) => !prev)}
              onPause={() => session?.pause()}
              onResume={() => session?.resume()}
              onStop={() => setIsStopSessionOpen(true)}
              onCancel={() => setIsCancelSessionOpen(true)}
            />
          </Card>

          <ConfirmationModal
            type="warning"
            open={isStopSessionOpen}
            onOpenChange={setIsStopSessionOpen}
            icon={<AlertTriangle className="size-8 text-destructive" />}
            title="Stop recording?"
            description="Once you stop the recording, the session will end and cannot be resumed. All recordings will stop, and the transcription will end."
            cancelButtonText="Cancel"
            confirmButtonText="Stop recording"
            onCancel={() => setIsStopSessionOpen(false)}
            onConfirm={handleSessionStopConfirm}
          />

          <ConfirmationModal
            type="warning"
            open={isCancelSessionOpen}
            onOpenChange={setIsCancelSessionOpen}
            icon={<AlertTriangle className="size-8 text-destructive" />}
            title="Cancel recording?"
            description="If you cancel now, all ongoing recordings will be permanently deleted and cannot be recovered."
            cancelButtonText="Continue Recording"
            confirmButtonText="Delete recording"
            onCancel={() => setIsCancelSessionOpen(false)}
            onConfirm={handleSessionCancelConfirm}
          />
        </div>
      )}

      <div
        className={cn(
          "relative flex min-h-0 flex-1 flex-col gap-4 overflow-hidden",
          showLiveView && "hidden"
        )}
      >
        {/* The Full-Transcript session Transcriber now lives in the root
            RecordingHost so the Azure stream survives tab-nav; this
            page drives it via the shared `transcriberRef` from the store and
            feeds it the live callback (registered above). Nothing to mount
            here anymore. */}

        {/* MDRS-575: shown on every section, so a failed transcription that can
            be re-run is never missed. */}
        {canRetryTranscription && (
          <TranscriptionFailedBanner
            isRetrying={retryTranscription.isPending}
            onRetry={requestRetryTranscription}
          />
        )}
        <ConfirmationModal
          type="warning"
          open={retryConfirmCase !== null}
          onOpenChange={(open) => {
            if (!open) setRetryConfirmCase(null);
          }}
          title="Retry the transcription?"
          description="The new transcription will replace what's in the transcript and summary sections, including anything typed into them since it failed. Assessment notes are kept."
          cancelButtonText="Cancel"
          confirmButtonText="Retry transcription"
          onCancel={() => setRetryConfirmCase(null)}
          onConfirm={handleRetryConfirm}
        />

        {/* Completion banner (MDRS-116, state D) — sits above the reused
            full-transcript viewer once a session stops. Swapped for the
            not-sent banner when the upload fails (MDRS-551). */}
        {/* The not-sent banner shows on every section, so a failure is never
            just a toast. */}
        {isSessionStopped &&
          (summarize.status === "failed" || summarize.status === "retrying" ? (
            <RecordingNotSentBanner
              hasAudio={summarize.hasAudio}
              isRetrying={summarize.status === "retrying"}
              onRetry={() => void dockControls.retrySummarize()}
              onDownload={dockControls.downloadRecording}
              onDiscard={dockControls.discardRecording}
            />
          ) : (
            isFullTranscript && (
              <RecordingCompleteBanner
                duration={completedDuration}
                isSending={summarize.status === "sending"}
                onDismiss={() => session?.reset()}
              />
            )
          ))}

        {/* Zero state (MDRS-113) — the Full Transcript panel before anything is
            recorded/uploaded. Replaces the panel body with a centered mic-pill,
            copy, and the Start Session / Upload Recording CTAs (which reuse the
            same session-start + upload flow as the header). Suppressed while a
            session is connecting/live/just-stopped, or once a transcript exists. */}
        {session &&
        isFullTranscript &&
        !isEditing &&
        !isModalSession &&
        !isSessionStopped &&
        !isSessionConnecting &&
        !sectionContent ? (
          <Card
            as="section"
            className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] border border-border bg-background-white p-0"
          >
            <TranscribeAndUploadButtons
              caseNumber={caseNumber}
              session={session}
              variant="empty-state"
            />
          </Card>
        ) : (
          <Card
            as="section"
            className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden rounded-[10px] border border-border bg-background-white p-0"
          >
            {/* Header (MDRS-114 — redesigned "rh" bar). Fill is the
              `background-raised` token (Figma #f8f9fc) — a near-white
              blue-grey, a hair lighter than the page background. */}
            <div className="flex shrink-0 items-center justify-between gap-4 border-b border-border bg-background-raised px-5 py-3.5">
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <h1 className="font-head text-sm font-semibold text-foreground">
                    {sectionTitle}
                  </h1>
                  {tooltipContent && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Info className="size-4 cursor-help text-foreground-muted" />
                      </TooltipTrigger>
                      <TooltipContent
                        className="max-w-sm text-wrap p-3"
                        side="bottom"
                        align="start"
                      >
                        {tooltipContent}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>
                {blurb}
                {isFullTranscript &&
                  currentAssessment?.transcription_timings?.total_s != null && (
                    <div className="flex items-center gap-1 text-xs text-foreground-muted">
                      <span>
                        Transcribed in{" "}
                        {formatDuration(
                          currentAssessment.transcription_timings.total_s * 1000
                        )}
                      </span>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Info className="size-3.5 cursor-help" />
                        </TooltipTrigger>
                        <TooltipContent
                          className="max-w-sm text-wrap p-3"
                          side="bottom"
                          align="start"
                        >
                          <div className="space-y-1">
                            {Object.entries(currentAssessment.transcription_timings)
                              .filter(([key]) => key !== "total_s")
                              .map(([key, value]) => (
                                <div
                                  key={key}
                                  className="flex justify-between gap-4"
                                >
                                  <span>{formatTimingStageLabel(key)}</span>
                                  <span>{value.toFixed(1)}s</span>
                                </div>
                              ))}
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </div>
                  )}
              </div>

              <div className="flex items-center gap-2">
                {/* Visible dictation button for the non-transcript sections.
                    Uses its OWN local ref — the shared store ref is
                    reserved for the recording-session Transcriber in the root
                    RecordingHost, so per-section dictation and the persistent
                    recording never fight over one Transcriber instance. */}

                {isEditing && (
                  <Button
                    variant="simple-outline"
                    size="sm"
                    onClick={handleCancel}
                    disabled={updateAssessmentField.isPending}
                  >
                    Cancel
                  </Button>
                )}

                {/* Edit/Save affordance. Hidden on the Full Transcript section —
                  it's read-only (the AI/recording output isn't hand-edited
                  inline; use Delete to clear it). Also hidden while a recording
                  session is live (the section shows via the collapsed pill). */}
                {!isFullTranscript && (!isModalSession || isEditing) && (
                  <Button
                    variant={isEditing ? "default" : "simple-outline"}
                    size="sm"
                    className="flex items-center gap-2"
                    isLoading={updateAssessmentField.isPending}
                    onClick={isEditing ? () => form.handleSubmit() : toggleEditing}
                  >
                    {isEditing ? (
                      updateAssessmentField.isPending ? (
                        "Saving"
                      ) : (
                        "Save"
                      )
                    ) : (
                      <>
                        {!updateAssessmentField.isPending && <FilePenLine />}
                        Edit
                      </>
                    )}
                  </Button>
                )}

                {isFullTranscript &&
                  !isEditing &&
                  !isModalSession &&
                  !!sectionContent && (
                    <Button
                      variant="destructive-outline"
                      size="compact"
                      onClick={() => setIsDeleteDialogOpen(true)}
                    >
                      Delete
                    </Button>
                  )}

                {!isFullTranscript && (
                  <Transcriber
                    ref={localTranscriberRef as React.Ref<TranscriberRef>}
                    callback={transcriptionHandler}
                    iconSize="lg"
                    showLabel
                    label="Start dictation"
                  />
                )}
              </div>
            </div>

            {/* Search + Speaker Filter — full transcript, read mode only.
              Horizontal padding matches the header bar above (px-5 / 16→20px in
              Figma this panel is 16px; we align to the shared header) and the
              gaps mirror the Figma "search-row" / "filter-row" spec: 8px between
              the input and Search button, ~10px between filter chips. */}
            {isFullTranscript && !isEditing && (
              <div className="shrink-0 space-y-3 px-5 py-3 text-sm">
                {/* Search queries the persisted transcript — hide it during a
                  live session (the transcript hasn't been saved yet). The
                  speaker filter below stays available for the live segments. */}
                {!isModalSession && (
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <Input
                        ref={searchInputRef}
                        value={searchQuery}
                        onChange={(e) => handleSearchQueryChange(e.target.value)}
                        onKeyDown={handleSearchKeyPress}
                        placeholder="Search transcript..."
                        leftIcon={
                          <Search className="size-4 text-foreground-muted" />
                        }
                        className={cn(
                          "h-10 min-h-10 w-full rounded-lg border-border bg-background-white shadow-none",
                          searchQuery.trim() && "pr-10"
                        )}
                      />
                      {searchQuery.trim() && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleClearSearch();
                          }}
                          className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 bg-background-white p-0"
                          aria-label="Clear search"
                        >
                          <X className="size-4" />
                        </Button>
                      )}
                    </div>
                    <Button
                      onClick={handleSearch}
                      disabled={!searchQuery.trim() || isSearching}
                      isLoading={isSearching}
                      size="sm"
                    >
                      Search
                    </Button>
                  </div>
                )}

                <div className="flex items-center gap-2.5">
                  <span className="text-foreground-muted">View By:</span>
                  <label
                    htmlFor="detailed-view-filter-all"
                    className="flex cursor-pointer items-center gap-1.5"
                  >
                    <Checkbox
                      id="detailed-view-filter-all"
                      checked={detailedViewFilter === "all"}
                      onCheckedChange={() => setDetailedViewFilter("all")}
                    />
                    <span>All</span>
                  </label>
                  {uniqueSpeakers.map(({ raw, display }) => (
                    <div key={raw} className="flex items-center gap-1">
                      <label
                        htmlFor={`detailed-view-filter-${raw}`}
                        className="flex cursor-pointer items-center gap-1.5"
                      >
                        <Checkbox
                          id={`detailed-view-filter-${raw}`}
                          checked={detailedViewFilter === display}
                          onCheckedChange={() => setDetailedViewFilter(display)}
                        />
                        {editingSpeaker?.raw === raw ? (
                          <Input
                            autoFocus
                            aria-label={`Edit name for ${display}`}
                            value={editingSpeaker.value}
                            onChange={(e) =>
                              setEditingSpeaker({ raw, value: e.target.value })
                            }
                            onBlur={commitSpeakerRename}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                commitSpeakerRename();
                              } else if (e.key === "Escape") {
                                e.preventDefault();
                                setEditingSpeaker(null);
                              }
                            }}
                            className="h-6 w-28 px-1 text-sm"
                          />
                        ) : (
                          <span>{display}</span>
                        )}
                      </label>
                      {/* Rename affordance — role/speaker labels are a
                          heuristic (see speaker_role_mapping.md, Track A);
                          let the user correct a wrong guess. */}
                      <button
                        type="button"
                        onClick={() => setEditingSpeaker({ raw, value: display })}
                        className="text-foreground-muted hover:text-foreground"
                        aria-label={`Rename ${display}`}
                      >
                        <Pencil className="size-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Content Area. Full Transcript is read-only (no inline edit), so
              it always renders the detailed read view below. */}
            {isEditing && !isFullTranscript ? (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden border-t border-border">
                <form.AppForm>
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      form.handleSubmit();
                    }}
                    className="flex h-full flex-col"
                  >
                    <form.AppField name="content">
                      {(field) => (
                        <field.TextAreaField
                          inputRef={textareaRef}
                          placeholder="Enter content for this section..."
                          className="h-full min-h-0 flex-1 resize-none rounded-none border-none bg-background-muted/40 p-4 !leading-loose"
                          wrapperClassName="flex h-full flex-col"
                          childrenContainerClassName="flex-1 flex flex-col"
                          disabled={updateAssessmentField.isPending}
                        />
                      )}
                    </form.AppField>
                  </form>
                </form.AppForm>
              </div>
            ) : (
              <ScrollArea
                ref={scrollAreaViewportRef}
                className={cn(
                  // Radix ScrollArea scrolls inside its own Viewport, so the Root
                  // must be height-bounded (flex-1 + min-h-0) and clip its
                  // overflow — never `overflow-y-auto`, which lets the Root grow
                  // to content height and breaks scrolling. Padding lives on the
                  // inner wrapper so it doesn't inflate the Viewport size.
                  "min-h-0 flex-1 overflow-hidden",
                  !isFullTranscript && "border-t border-border"
                )}
                onMouseUp={handleMouseUp}
              >
                <div className="p-4">
                  {/* Plain view — non-transcript sections only */}
                  {!isFullTranscript && (
                    <PlainTranscriptContent
                      isFetching={isFetching}
                      sectionContent={sectionContent}
                      isAssessmentNotesSection={isAssessmentNotesSection}
                      isHighlighted={isPlainContentHighlighted}
                    />
                  )}

                  {/* Detailed view — full transcript only (MDRS-86: sole view) */}
                  {isFullTranscript && (
                    <DetailedTranscriptContent
                      isSearching={isSearching}
                      searchError={searchError}
                      searchResults={searchResults}
                      detailedEntries={detailedEntries}
                      detailedViewFilter={detailedViewFilter}
                      highlightedEntryId={highlightedEntryId}
                      onSearchResultClick={handleSearchResultClick}
                      isLive={isModalSession}
                      isTranscribing={
                        (fieldStatus || currentAssessment?.status) ===
                        ASSESSMENT_STATUS.TRANSCRIBING
                      }
                      isFailed={
                        (fieldStatus || currentAssessment?.status) ===
                        ASSESSMENT_STATUS.FAILED
                      }
                    />
                  )}
                </div>
              </ScrollArea>
            )}
          </Card>
        )}
      </div>

      {/* MDRS-401: this used to be TWO independent, uncoordinated crash-recovery
          modals stacked here — this SCRIBE-05 audio-orphan prompt, and a
          separate "Restore unsaved dictation?" text-only modal (removed
          entirely, not replaced — its "Restore" never actually persisted
          anything, see the ticket for why). This audio prompt is now the only
          Azure recovery surface. Gated on `caseData` (not the raw route param)
          so it only ever fires once the backend's own case-read authorization
          has actually succeeded for the current user — this page's route
          param alone proves nothing about access. Also suppressed while a
          live session is already recording for this case (the store-based
          check), and while the `session` prop itself is non-idle even though
          the store hasn't synced yet (a remount-during-recording race) — an
          orphan found in that window belongs to the CURRENT session, not a
          crash. Waits for the AssemblyAI prompt below to settle so the two
          never open stacked. */}
      {caseData &&
        !isRecordingLiveForThisCase &&
        !isSessionPropInFlight &&
        assemblyAIRecoverySettledFor === recoveryCheckKey && (
          <RecordingRecoveryPrompt
            key={`azure-${recoveryCheckKey}`}
            caseNumber={caseNumber}
          />
        )}
      {/* Mounted regardless of the flag so orphans survive a flag flip. */}
      {caseData &&
        ((!isRecordingLiveForThisCase && !isSessionPropInFlight) ||
          isResumingRecording) && (
          <AssemblyAIRecoveryPrompt
            key={`assemblyai-${recoveryCheckKey}`}
            caseNumber={caseNumber}
            onSettled={(settledCase) =>
              setAssemblyAIRecoverySettledFor(
                `${settledCase}#${recoveryCheckRequestedAt}`
              )
            }
            onResume={isAssemblyAIScribe() ? handleResumeRecording : undefined}
          />
        )}

      {/* MDRS-399 — confirm before clearing the transcript and everything derived from it. */}
      <ConfirmationModal
        type="warning"
        icon={<Trash2 className="size-10 text-destructive" />}
        title="Delete transcript?"
        description="This permanently removes the transcript, summary, and all report sections for this assessment. This cannot be undone."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        isLoading={updateAssessment.isPending}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDeleteTranscript}
        onCancel={() => setIsDeleteDialogOpen(false)}
      />

      {selectedText && (
        <SnipResultPopover
          title="Selected Text"
          text={selectedText}
          isLoading={false}
          isProcessing={false}
          error={null}
          anchorRect={selectionRect}
          onClose={handleSelectionClose}
          onMapToReportBuilder={handleMapToReportBuilder}
        />
      )}

      <MapToReportBuilderDialog
        open={isMapDialogOpen}
        onOpenChange={setIsMapDialogOpen}
        text={mapDialogText}
        targetField="summary_from_assessment_scribe"
        caseNumber={caseNumber}
        templateId={templateId}
        extractType="highlight"
        categorySlug={sectionKey}
      />
    </>
  );
};
